// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import loading from '../../../share/components/vue/ProcessingIcon';
import individualDiagnose from '../../../share/util/individualDiagnose';
import config from '../../../share/config';
import apiService from '../../../share/util/api/service';
import { SP_KEY_INFO, SUPPORTED_FEATURES, PHASE } from './inputSpecialKey';
import DiagSystemUtils from '@/common/nodejs/diagSystemUtils';
import { getAbbrSysName } from '../../../share/util/systemName';

export default {
  name: "individualSelect",
  data() {
    return {
      processing: false,
      individualListItems: [],
      inputKeyPhase: '',
      keyCode: new Set([]),
      checkPasswordFunctionList: null,
      showInputPasswordModal: false,
      isLoading: false,
      password: '',
      validateParam: undefined
    };
  },

  watch: {
    language: {
      immediate: true,
      handler(val) {
        val; // To avoid warning
      }
    },
  },

  components: {
    'processing-view': loading,
  },

  // router params 対応
  props: ["systemId"],

  methods: {
    ...mapActions('auth', ['logout']),
    ...mapActions('vehicle', ['setDisplaySpecialFunction']),

    /**
     * アイコンのクリック処理
     */
    clickItem(routeName) {
      const routerData = {
        name: routeName,
        params: {
          systemId: this.systemId
        }
      };
      // 初期化通信有無のための同じシステム内遷移判定
      if (this.$route.query && Object.prototype.hasOwnProperty.call(this.$route.query, 'sameSystemFlg')) {
        routerData.query = { sameSystemFlg: this.$route.query.sameSystemFlg };
      }
      this.$router.push(routerData);
      // 画面上部へのアンカースクロール
      document.getElementById('scrollTabBlock').scrollIntoView({ behavior: 'instant' });
    },

    /**
     * 診断機能一覧のリストを更新する
     */
    updateIndividualTable() {
      const menuList = this.individualMenus ? this.individualMenus.menu_list : [];
      const systemId = this.systemId;
      let targetMenu = null;

      // DTC/制御作動履歴 統合メニュー判定
      targetMenu = menuList.find(menu => menu.system_name === systemId);
      const isCombined = individualDiagnose.areDiagAndVchEnabled(targetMenu);

      const individualList = individualDiagnose.filterIndividualFunctionMap(targetMenu, isCombined).map((item) => {
        const returnData = {
          name: item.label,
          route_name: item.type
        };

        // 特殊機能表示できる機能一覧を取得
        const systemSpFuncList = this.getSpecialFeaturesList(systemId);
        if (systemSpFuncList) {
          // 特殊機能表示できる機能一覧があるとき、「*」を付与
          return this.updateIndividualTableItem(returnData, targetMenu, item, systemSpFuncList);
        } else {
          return returnData;
        }
      });
      // 統合メニューは先頭に追加
      if (isCombined) {
        individualList.unshift({
          name: 'individual.individual_diagnosis_function_dtc_with_vch',
          route_name: config.INDIVIDUAL_FUNCTION_DTC_VCH
        });
      }
      this.individualListItems = individualList;
    },

    /**
     * 現在のシステムで特殊機能表示できる機能一覧を返却する関数
     * @param {string} systemName システム名
     * @returns {array} 現在のシステムで特殊機能表示できる機能一覧
     */
    getSpecialFeaturesList(systemName) {
      // 特殊機能表示できるECU
      const parameterList = [
        { match: DiagSystemUtils.isEyesightByAbbreviationId, convertedSystemName: 'ES' },
        { match: DiagSystemUtils.isAdasEcuByAbbreviationId, convertedSystemName: 'ADAS' }
      ];

      // オブジェクトから取得できるよう現在のシステム名を変換
      const converted = parameterList.find(({ match }) => match(getAbbrSysName(systemName)))?.convertedSystemName ?? null;
      // オブジェクトから取得した配列を返却
      return converted ? SUPPORTED_FEATURES.find(item => item[converted] !== undefined)?.[converted] : null;
    },

    /**
     * 特殊キー入力による機能設定更新
     * @param {object} returnData 各システムで表示される機能の設定内容
     * @param {object} targetMenu 各システムのメニュー表示設定
     * @param {object} item 診断個別システム表示用マップ
     * @returns 各システムで表示される機能の設定内容
     */
    updateIndividualTableItem(returnData, targetMenu, item, systemSpFuncList) {
      const specialFunc = this.displaySpecialFunction.find(display => display.key === item.key);
      // 各特殊キー対応システムにて表示可能な機能のみ特殊機能を表示する
      if (specialFunc && (systemSpFuncList.includes(specialFunc.type))) {
        // 特殊機能のダイアグコード、キャンセルコードの場合はspecial_feature_menu_display_configを確認する
        if (!targetMenu.special_feature_menu_display_config) {
          // 表示不可：通常機能を表示する
          return returnData;
        }

        // データモニタの場合は現状だとspecial_feature_menu_display_configに定義がないため、keyが存在しなくても個別診断のツリーを作成する(定義は将来対応)
        if (specialFunc.type === config.SPECIAL_FEATURE_DATA_MONITOR) {
          returnData.isSpecial = true;
          returnData.route_name = specialFunc.type;
          return returnData;
        } else {
          // special_feature_menu_display_configに対象の個別診断機能のkeyが存在し、値がtrueの場合、個別診断のツリーを作成する
          if (Object.keys(targetMenu.special_feature_menu_display_config).some(individualKey =>
            individualKey === specialFunc.key &&
            (targetMenu.special_feature_menu_display_config[individualKey])
          )) {
            returnData.isSpecial = true;
            returnData.route_name = specialFunc.type;
            return returnData;
          }
        }
      }
      return returnData;
    },

    /**
     * 特殊キー入力表示文言
     */
    displayMessage() {
      const messageMap = {
        inputPassword: 'individual.input_password_spws',
        inputError: 'individual.discrepancy_password_spws'
      };
      return this.$t(messageMap[this.inputKeyPhase]);
    },

    /**
     * KeyUpイベント
     * @param {Object} event イベント
     */
    async onKeyUp(event) {
      // キーコードがあればその要素を削除
      if (this.inputKeyPhase === '') {
        this.keyCode.delete(this.codeFromEvent(event));
        if (this.keyCode.size === 3) { await this.checkInputKey(); }
      }
    },

    /**
     * KeyDownイベント
     * @param {Object} event イベント
     */
    async onKeyDown(event) {
      // 処理中の場合スキップ
      if (this.inputKeyPhase === '') {
        this.keyCode.add(this.codeFromEvent(event));
        if (this.keyCode.size === 3) { await this.checkInputKey(); }
      }
    },

    /**
     * パスワード入力画面でOK押下
     */
    async onInputKeyOk() {
      // 入力規則チェック
      if (this.validate()) {
        // パスワード判定
        await this.passwordVerification();
      }
    },

    /**
     * 特殊キーチェック
     */
    async checkInputKey() {
      // 特殊機能が表示できるECUではない場合、処理を中断
      if (!this.getSpecialFeaturesList(this.systemId)) {
        return;
      }
      this.checkPasswordFunctionList = SP_KEY_INFO.filter(info => {
        // 表示済みではない機能かつ指定の特殊キー押下
        const isShowed = this.displaySpecialFunction.some(d => d.type === info.type);
        return !isShowed && info.code.every(c => this.keyCode.has(c));
      }
      );

      if (this.checkPasswordFunctionList.length > 0) {
        this.inputKeyPhase = PHASE.INPUT_PASSWORD;
        this.showInputPasswordModal = true;
      }
    },

    /**
     * パスワード入力のバリデーション
     */
    validate() {
      this.validateParam = undefined;

      // 入力したパスワードが入力規則に沿っているか確認(半角数字のみ)
      const regex = new RegExp(/^[0-9]*$/);
      if (!regex.test(this.password) || this.password === '') {
        // 入力規則に沿っていない場合はアラートを表示する
        this.validateParam = { valid: true, msg: this.$t(`message.alert_validate_invalid_pattern`) };
        return false;
      } else {
        return true;
      }
    },

    /**
     * キーコード取得
     * @param {Object} ev イベント
     */
    codeFromEvent(ev) {
      return ev.keyCode || '';
    },

    /**
     * パスワードチェック
     */
    async passwordVerification() {
      try {
        this.isLoading = true;
        const endPoint = '/password';
        const requestBody = {
          password: this.password,
          screen: this.$route.name,
          // 配列内のキーコードは同じであるためそのうちの一つを指定しチェックを行う
          special_key: this.checkPasswordFunctionList[0].uniqueKey
        };
        const pinVerifiedRes = await apiService.postCall(apiService.DIAG, endPoint, requestBody);

        // チェック結果を返す
        if (pinVerifiedRes?.data?.verified) {
          // storeに特殊機能表示状態を保持
          this.setDisplaySpecialFunction(this.checkPasswordFunctionList);
          // メニュー一覧を更新
          this.updateIndividualTable();
          this.initInputKeyInfo();
        } else {
          // パスワード入力エラー画面に遷移
          this.inputKeyPhase = PHASE.INPUT_ERROR;
        }
      } finally {
        this.isLoading = false;
      }
    },


    /**
     * 特殊キー処理状態を初期化
     */
    initInputKeyInfo() {
      // モーダル非表示
      this.showInputPasswordModal = false;
      // パスワード初期化
      this.password = '';
      this.validateParam = undefined;
      // キー入力状態初期化
      this.inputKeyPhase = '';
      this.checkPasswordFunctionList = null;
      this.keyCode = new Set([]);
    },

    /**
     * OK押下時の処理
     */
    onOkClicked() {
      switch (this.inputKeyPhase) {
        case PHASE.INPUT_PASSWORD:
          this.onInputKeyOk();
          break;
        case PHASE.INPUT_ERROR:
          this.initInputKeyInfo();
          break;
        default:
          break;
      }
    },

  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      individualMenus: state => state.vehicle.individualMenus,
      displaySpecialFunction: state => state.vehicle.displaySpecialFunction
    }),
    /**
     * 選択しているシステム名を返却
     */
    selectedSystemName() {
      return `diagSytem.${this.systemId}`;
    },

    /**
     * 特殊キー入力ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.inputKeyPhase) {
        case PHASE.INPUT_ERROR:
          return 'danger';
        case PHASE.INPUT_PASSWORD:
        default:
          return 'secondary';
      }
    },

    /**
     * 特殊キー入力ダイアログのヘッダータイトル
     */
    headerTitle() {
      const titleMap = {
        inputPassword: 'message.common_title_confirmation',
        inputError: 'message.common_title_error'
      };
      return this.$t(titleMap[this.inputKeyPhase]);
    },

    /**
     * 特殊キー入力ダイアログにて入力欄/キャンセルボタンを表示するかどうか
     * ※どちらもパスワード入力画面でのみ表示
     */
    showInputFormAndCancelButton() {
      const showInputAndButtonMap = {
        inputPassword: true
      };
      let result = showInputAndButtonMap[this.inputKeyPhase];
      if (typeof result === 'undefined') {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },
  },

  created() {
  },

  mounted() {
    // ツリーから診断機能ノードが選択されて遷移してきた時
    if (this.$route.query && Object.prototype.hasOwnProperty.call(this.$route.query, 'funcId')
      && Object.prototype.hasOwnProperty.call(this.$route.query, 'sameSystemFlg')
      && Object.prototype.hasOwnProperty.call(this.$route.params, 'systemId')) {
      // 個別診断の選択された機能に遷移する
      individualDiagnose.moveIndividualDiagnoseScreen(
        this,
        this.$route.query.funcId,
        this.systemId,
        this.$route.query.sameSystemFlg,
      );
      return;
    }

    // タブ制御
    this.$emit("setVehicleTabsAvailable", true);
    this.updateIndividualTable();
    // イベントリスナーの登録
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('keyup', this.onKeyUp);
  },

  beforeDestroy() {
    // イベントリスナーの削除
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);
  },
};
